/**
 * File: adr.ts
 * Description: TypeScript type definitions for Architecture Decision Records (ADRs)
 * Author: System
 * Created: 2025-08-09
 * Last Modified: 2025-10-08
 */

export interface ADR {
  id: string;
  number: string; // ADR-001, ADR-002, etc.
  title: string;
  status: ADRStatus;
  source: ADRSource;
  confidenceScore?: number; // For AI suggestions (0-100)
  contextSource?: string; // Which document/analysis triggered the suggestion
  
  // Content sections
  decisionStatement: string;
  contextBackground: string;
  optionsConsidered: ADROption[];
  decisionRationale: string;
  consequences: ADRConsequence[];
  dependencies: string[]; // References to other ADRs
  monitoringCriteria: string[];
  
  // Generated content
  generatedContent?: string; // Full markdown content of generated ADR
  sourceDefinitionId?: string; // Links back to the ADR definition that created this
  
  // Metadata
  decisionDate?: Date;
  reviewDate?: Date;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  assignedTo?: string[];
  
  // File management
  fileName: string;
  filePath: string;
  version: number;
}

export enum ADRStatus {
  SELECTED = 'selected',
  IN_PROGRESS = 'in_progress',
  COMPLETE = 'complete',
  UNDER_REVIEW = 'under_review',
  APPROVED = 'approved',
  DEPRECATED = 'deprecated'
}

export enum ADRSource {
  AI_SUGGESTED = 'ai_suggested',
  CONTEXT_IMPORTED = 'context_imported',
  USER_CREATED = 'user_created'
}

export interface ADROption {
  id: string;
  title: string;
  description: string;
  pros: string[];
  cons: string[];
  estimatedCost?: string;
  technicalComplexity?: 'low' | 'medium' | 'high';
}

export interface ADRConsequence {
  id: string;
  type: 'positive' | 'negative' | 'neutral';
  description: string;
  impact: 'low' | 'medium' | 'high';
  mitigation?: string;
}

export interface ADRSuggestion {
  id: string;
  title: string;
  description: string;
  confidenceScore: number;
  contextSource: string;
  reasoning: string;
  priority?: string;
  category?: string;
  suggestedContent?: Partial<ADR>;
}

export interface ADRTemplate {
  id: string;
  name: string;
  description: string;
  template: Partial<ADR>;
  isDefault: boolean;
}

export interface ADRQualityMetrics {
  completenessScore: number; // 0-100
  clarityScore: number; // 0-100
  consistencyScore: number; // 0-100
  bestPracticeCompliance: number; // 0-100
  missingFields: string[];
  suggestions: string[];
}

export interface ADRDependency {
  fromAdrId: string;
  toAdrId: string;
  dependencyType: 'requires' | 'influences' | 'conflicts' | 'supersedes';
  description?: string;
}

export interface ADRConflict {
  adr1Id: string;
  adr2Id: string;
  conflictType: 'contradictory' | 'overlapping' | 'incompatible';
  description: string;
  severity: 'low' | 'medium' | 'high';
  suggestedResolution?: string;
}

export interface ADRProjectSummary {
  totalAdrs: number;
  adrsByStatus: Record<ADRStatus, number>;
  overallHealthScore: number;
  completionPercentage: number;
  lastUpdated: Date;
}

export interface ADRFilterState {
  sources: ADRSource[];
  statuses: ADRStatus[];
  searchQuery: string;
  sortBy: 'relevance' | 'created' | 'updated';
  sortOrder: 'asc' | 'desc';
}

export interface ADRBulkAction {
  action: 'generate' | 'export' | 'delete' | 'approve' | 'prioritize';
  adrIds: string[];
  options?: Record<string, any>;
}

export interface ADRDefinition {
  id: string;
  title: string;
  llmInstructions: string;
  contextDescription?: string;
  source: ADRSource
  status: ADRDefinitionStatus;
  createdDate: string;
  updatedDate: string;
  // User-editable governance fields. The agent NEVER fills these; the user
  // enters them manually in the My ADRs table. Empty until the user fills them.
  stepOut?: string;
  approvalDate?: string;
  approvedBy?: string;
  // Resolved IT4IT Application ID (APMID) of the project's main in-scope
  // application, used for BiZZdesign import. Empty when not resolvable.
  relatedApmid?: string;
}

export interface ADRDefinitionUpsert {
  title: string;
  llmInstructions: string;
  contextDescription?: string;
  source: ADRSource
  status: ADRDefinitionStatus;
  // User-editable governance fields (optional; preserved on update).
  stepOut?: string;
  approvalDate?: string;
  approvedBy?: string;
}

export enum ADRDefinitionStatus {
  DEFINED = 'defined', // Ready for generation
  GENERATED = 'generated', // ADR has been created
  IN_REVIEW = 'in_review' // Being edited/reviewed
}
